import { Alert, Box, IconButton, Snackbar, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../utils/hooks';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchProducts } from '../../modules/products/operations';
import { getProducts } from '../../modules/products/selectors';
import { Product, ProductVariant } from '../../modules/products/models';
import { EloSecondaryButton } from '../../components/BergButtons';
import { PaymentButton } from '../../components/styled-components';
import { incrementProduct, decrementProduct } from '../../modules/basket/slice';
import { getBasketProducts } from '../../modules/basket/selectors';
import { getEmbeddedQrScanner } from '../../modules/layout/selectors';

interface ParsedRfidCode {
  productCode: string;
  shelf?: string;
}

interface ScannedItem {
  product: Product;
  variant: ProductVariant;
  quantity: number;
  shelf?: string;
}

interface GroupedItem {
  key: string;
  name: string;
  category: string;
  price: number;
  currency: string;
  quantity: number;
  shelf?: string;
  items: ScannedItem[];
}

const RfidScanner = () => {
  const { t } = useTranslation(['common', 'pages']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const products = useAppSelector((s) => getProducts(s.product));
  const basketProducts = useAppSelector((s) => getBasketProducts(s.basket));
  const embeddedQrScanner = useAppSelector((x) => getEmbeddedQrScanner(x.layout));

  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [openError, setOpenError] = useState<boolean>(false);

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  const formatShelfDisplay = (shelf?: string): string | undefined => {
    if (!shelf) return undefined;
    const match = shelf.match(/^shelf-(\d+)$/);
    if (match) {
      return `Regał ${match[1]}`;
    }
    return shelf;
  };

  // Group items by name, variant name (category), and shelf
  const groupedItems = useMemo((): GroupedItem[] => {
    const groups = new Map<string, GroupedItem>();

    scannedItems.forEach((item) => {
      const key = `${item.product.name}-${item.variant.name}-${item.shelf || ''}`;
      const existing = groups.get(key);

      if (existing) {
        existing.quantity += item.quantity;
        existing.items.push(item);
      } else {
        groups.set(key, {
          key,
          name: item.product.name,
          category: item.variant.name,
          price: item.variant.price.amount,
          currency: item.variant.price.currency,
          quantity: item.quantity,
          shelf: item.shelf,
          items: [item],
        });
      }
    });

    return Array.from(groups.values());
  }, [scannedItems]);

  const isHexString = (str: string): boolean => {
    // Check if string is hex: even length and only hex characters
    return str.length > 0 && str.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(str);
  };

  const hexToAscii = (hex: string): string => {
    let result = '';
    for (let i = 0; i < hex.length; i += 2) {
      const charCode = parseInt(hex.substr(i, 2), 16);
      // Skip null bytes
      if (charCode !== 0) {
        result += String.fromCharCode(charCode);
      }
    }
    return result;
  };

  const parseRfidCode = (code: string): ParsedRfidCode => {
    // Format: dp-1001-s-01 or dp-1001 (shelf is optional)
    // Use non-greedy match and don't require end of string to handle garbage data
    const shelfMatch = code.match(/(dp-\d+)(-s-(\d+))?/);
    if (shelfMatch) {
      return {
        productCode: shelfMatch[1],
        shelf: shelfMatch[3] ? `shelf-${parseInt(shelfMatch[3], 10)}` : undefined,
      };
    }
    return { productCode: code };
  };

  const parseRfidCodes = (data: string): ParsedRfidCode[] => {
    if (!data) return [];

    let processedData = data;

    // Detect if hex and convert to ASCII
    if (isHexString(data)) {
      processedData = hexToAscii(data);
      console.log('Hex to ASCII:', data, '->', processedData);
    }

    // Extract all valid dp-XXXX or dp-XXXX-s-XX patterns from the data
    const pattern = /dp-\d+(-s-\d+)?/g;
    const matches = processedData.match(pattern);

    if (matches && matches.length > 0) {
      return matches.map((match) => parseRfidCode(match));
    }

    return [];
  };

  const findProductByRfidCode = (rfidCode: string): { product: Product; variant: ProductVariant } | null => {
    console.log('Looking for rfidCode:', JSON.stringify(rfidCode), 'in', products.length, 'products');
    console.log('Available rfidCodes:', products.map(p => ({ name: p.name, rfidCode: p.rfidCode })).filter(p => p.rfidCode));
    for (const product of products) {
      if (product.rfidCode === rfidCode) {
        if (product.variants && product.variants.length > 0) {
          return { product, variant: product.variants[0] };
        }
      }
    }
    return null;
  };

  const handleRfidScan = useCallback(
    (data: string) => {
      if (!data) {
        console.log('Empty RFID data received');
        return;
      }

      console.log('RFID scan received:', data);
      const rfidCodes = parseRfidCodes(data);
      console.log('Parsed RFID codes:', rfidCodes);

      const notFoundCodes: string[] = [];

      rfidCodes.forEach((parsedCode) => {
        const result = findProductByRfidCode(parsedCode.productCode);
        if (result) {
          const { product, variant } = result;
          dispatch(incrementProduct([variant, product]));

          setScannedItems((prev) => {
            const existingIndex = prev.findIndex(
              (item) =>
                item.product.id === product.id &&
                item.variant.id === variant.id &&
                item.shelf === parsedCode.shelf
            );
            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = {
                ...updated[existingIndex],
                quantity: updated[existingIndex].quantity + 1,
              };
              return updated;
            } else {
              return [...prev, { product, variant, quantity: 1, shelf: parsedCode.shelf }];
            }
          });
        } else {
          console.log('No product found for RFID code:', parsedCode.productCode);
          notFoundCodes.push(parsedCode.productCode);
        }
      });

      if (notFoundCodes.length > 0) {
        setErrorMessage(`${t('common:rfidScanner.productNotFound')}: ${notFoundCodes.join(', ')}`);
        setOpenError(true);
      }
    },
    [products, dispatch, t]
  );

  useEffect(() => {
    if (window.BtApp) {
      console.log('Assign RFID handleQrReader');
      window.BtApp.handleQrReader = handleRfidScan;
    }
    return () => {
      if (window.BtApp && window.BtApp.handleQrReader) {
        console.log('Unregister RFID handleQrReader func');
        window.BtApp.handleQrReader = undefined;
      }
    };
  }, [embeddedQrScanner, handleRfidScan]);

  const handleIncrement = (group: GroupedItem) => {
    const item = group.items[0];
    dispatch(incrementProduct([item.variant, item.product]));
    setScannedItems((prev) =>
      prev.map((i) =>
        i.product.id === item.product.id && i.variant.id === item.variant.id
          ? { ...i, quantity: i.quantity + 1 }
          : i
      )
    );
  };

  const handleDecrement = (group: GroupedItem) => {
    const item = group.items[0];
    dispatch(decrementProduct(item.variant));
    setScannedItems((prev) => {
      const updated = prev.map((i) =>
        i.product.id === item.product.id && i.variant.id === item.variant.id
          ? { ...i, quantity: i.quantity - 1 }
          : i
      );
      return updated.filter((i) => i.quantity > 0);
    });
  };

  const handleCloseError = () => {
    setOpenError(false);
  };

  const totalAmount = scannedItems.reduce(
    (sum, item) => sum + item.variant.price.amount * item.quantity,
    0
  );

  const totalQuantity = scannedItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Stack direction="column" sx={{ height: '100%', py: 1 }} spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <EloSecondaryButton onClick={() => navigate('/')}>
          {t('common:buttons.back')}
        </EloSecondaryButton>
      </Stack>

      <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, p: 1.5, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: '#3498DB', fontWeight: 'bold' }}>
          {t('common:rfidScanner.useRfidScanner')}
        </Typography>
      </Box>

      <Stack
        direction="column"
        spacing={0.5}
        sx={{ flex: 1, overflow: 'auto', backgroundColor: '#fafafa', borderRadius: 1, p: 1 }}
      >
        {groupedItems.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 2 }}>
            {t('common:rfidScanner.noScannedItems')}
          </Typography>
        ) : (
          groupedItems.map((group) => (
            <Box
              key={group.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: 'white',
                borderRadius: 1,
                px: 1.5,
                py: 1,
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" fontWeight="bold" noWrap>
                  {group.name}
                </Typography>
                {group.category && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {group.category} - {group.price} {group.currency}
                  </Typography>
                )}
                {group.shelf && (
                  <Typography variant="body2" color="primary" noWrap>
                    {formatShelfDisplay(group.shelf)}
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => handleDecrement(group)}
                  sx={{ backgroundColor: '#EE4A83', color: 'white', '&:hover': { backgroundColor: '#d43d73' } }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ minWidth: 30, textAlign: 'center', fontSize: '1.2rem', fontWeight: 'bold' }}>
                  {group.quantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleIncrement(group)}
                  sx={{ backgroundColor: '#27AE60', color: 'white', '&:hover': { backgroundColor: '#1e8449' } }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          ))
        )}
      </Stack>

      {scannedItems.length > 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="column">
            <Typography variant="body1">
              {t('common:rfidScanner.totalItems')}: {totalQuantity}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {t('common:rfidScanner.totalAmount')}: {totalAmount} PLN
            </Typography>
          </Stack>
          <PaymentButton onClick={() => navigate('/summary?r=/rfid-scan')}>
            {t('common:buttons.payment')}
          </PaymentButton>
        </Stack>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openError}
        onClose={handleCloseError}
        autoHideDuration={3000}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default RfidScanner;
